import { NormalizedCacheObject } from 'apollo-boost';
import { History } from 'history';
import createRavenMiddleware from 'raven-for-redux';
// tslint:disable-next-line import-blacklist
import Raven from 'raven-js';
import { applyMiddleware, compose, createStore, Middleware, Store, StoreEnhancer } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createEpicMiddleware, EpicMiddleware } from 'redux-observable';
import { persistStore } from 'redux-persist';

import { Action } from '../actions';
import { BannersDataProvider } from '../api/banners/BannersDataProvider';
import { BannersMutationExecutor } from '../api/banners/BannersMutationExecutor';
import { SitelinksMutationExecutor } from '../api/banners/SitelinksMutationExecutor';
import { CalloutsMutation } from '../api/callouts/CalloutsMutation';
import { CampaignsDataProvider } from '../api/campaigns/CampaignsDataProvider';
import { CampaignsMutationExecutor } from '../api/campaigns/CampaignsMutationExecutor';
import { ClientDevProvider } from '../api/ClientDevProvider/ClientDevProvider';
import { CommonDataProvider } from '../api/CommonDataProvider';
import { DealsDataProvider } from '../api/deals/DealsDataProvider';
import { DialogsDataProvider } from '../api/dialogs/DialogsDataProvider';
import { DialogsMutationExecutor } from '../api/dialogs/DialogsMutationExecutor';
import { DNAApolloClient } from '../api/DNAApolloClient/DNAApolloClient';
import { DocCenterRestApiMethodExecutor } from '../api/DocCenterRestApiMethodExecutor/DocCenterRestApiMethodExecutor';
import { GoalsDataProvider } from '../api/goals/GoalsDataProvider';
import { EntitesDataProvider } from '../api/grid/EntitiesDataProvider';
import { FreelancerDataProvider } from '../api/grid/FreelancerDataProvider';
import { FreelancerMutationExecutor } from '../api/grid/FreelancerMutationExecutor';
import { PhrasesMutationExecutor } from '../api/grid/PhrasesMutationExecutor/PhrasesMutationExecutor';
import { PhrasesWizardDataProvider } from '../api/grid/PhrasesWizardDataProvider';
import { PricesWizardMutationExecutor } from '../api/grid/PricesWizardMutationExecutor';
import { GroupsDataProvider } from '../api/groups/GroupsDataProvider';
import { GroupsMutationExecutor } from '../api/groups/GroupsMutationExecutor';
import { ImportProvider } from '../api/ImportProvider';
import { IncognitoDataProvider } from '../api/IncognitoDataProvider';
import { LogDataSource } from '../api/log/LogDataSource';
import { MarketersDataProvider } from '../api/MarketersDataProvider';
import { MinusPhraseLibraryProvider } from '../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { MinusPhraseMutationExecutor } from '../api/MinusPhraseLibraryProvider/MinusPhraseMutationExecutor';
import { OrganizationDataProvider } from '../api/organization/OrganizationDataProvider';
import { OrganizationMutationExecutor } from '../api/organization/OrganizationMutationExecutor';
import { PredictorProvider } from '../api/predictor/PredictorProvider';
import { PricePackagesDataProvider } from '../api/pricePackages/pricePackagesDataProvider';
import { PricePackagesMutationExecutor } from '../api/pricePackages/pricePackagesMutationExecutor';
import { ProductsDataProvider } from '../api/ProductsDataProvider';
import { RecommendationsDataProvider } from '../api/recommendations/RecommendationsDataProvider';
import { RecommendationsMutationExecutor } from '../api/recommendations/RecommendationsMutationExecutor';
import { RegionsDataProvider } from '../api/regions/RegionsDataProvider';
import { RegionsMutationExecutor } from '../api/regions/RegionsMutationExecutor';
import { ReportsDomainDataProvider } from '../api/reports/ReportsDomainDataProvider';
import { RestApiManager } from '../api/RestApiManager';
import { RetargetingDataProvider } from '../api/retargeting/RetargetingDataProvider';
import { RetargetingMutationExecutor } from '../api/retargeting/RetargetingMutationExecutor';
import { SmartFiltersDataProvider } from '../api/smartFilters/SmartFiltersDataProvider';
import { SmartFiltersMutationExecutor } from '../api/smartFilters/SmartFiltersMutationExecutor';
import { TurboDataProvider } from '../api/TurboDataProvider/TurboDataProvider';
import { UserSettingsDataProvider } from '../api/UserSettingsDataProvider';
import { XlsDataProvider } from '../api/XlsDataProvider';
import { CanvasApi } from '../canvas/api/CanvasApi';
import { mainEpic } from '../epics/index';
import { getRootReducer, InitializationData } from '../reducers';
import { enhancer, middleware } from '../routes/index';
import { routesMiddleware } from '../routes/routesMiddleware';
import { State } from '../typings/state';

import { metrikaMiddleware } from './Metrika/metrika';
import { rumErrorCounterMiddleWare } from './RUM/RUMErrorCounterMiddleware';
import { rumMiddleWare } from './RUM/RUMMiddleWare';
import { SegmentManager } from './SegmentManager';

export function getStore(
    apiManager: RestApiManager,
    history: History,
    epicDeps: EpicDeps,
    initData: InitializationData
): Store<State> {
    const epicMiddleware = createEpicMiddleware<Action, Action, State, EpicDeps>({
        dependencies: epicDeps
    });

    const store = createStore(
        getRootReducer(initData),
        compose(
            enhancer,
            createMiddleware(apiManager, history, epicMiddleware)
        )
    );

    epicMiddleware.run(mainEpic);

    persistStore(store);

    return store;
}

export interface EpicDeps {
    client: DNAApolloClient<NormalizedCacheObject>;
    entitiesDataProvider: EntitesDataProvider;
    recommendationsDataProvider: RecommendationsDataProvider;
    commonDataProvider: CommonDataProvider;
    segmentManager: SegmentManager;
    logDataProvider: LogDataSource;
    docCenterApi: DocCenterRestApiMethodExecutor;
    restApiManager: RestApiManager;
    pricesWizardMutationExecutor: PricesWizardMutationExecutor;
    phrasesMutationExecutor: PhrasesMutationExecutor;
    freelancerMutationExecutor: FreelancerMutationExecutor;
    freelancerDataProvider: FreelancerDataProvider;
    recommendationsMutationExecutor: RecommendationsMutationExecutor;
    dealsDataProvider: DealsDataProvider;
    pricePackagesDataProvider: PricePackagesDataProvider;
    pricePackagesMutationExecutor: PricePackagesMutationExecutor;
    bannersDataProvider: BannersDataProvider;
    campaignsDataProvider: CampaignsDataProvider;
    campaignsMutationExecutor: CampaignsMutationExecutor;
    groupsDataProvider: GroupsDataProvider;
    groupsMutationExecutor: GroupsMutationExecutor;
    smartFiltersMutationExecutor: SmartFiltersMutationExecutor;
    smartFiltersDataProvider: SmartFiltersDataProvider;
    turboDataProvider: TurboDataProvider;
    bannersMutationExecutor: BannersMutationExecutor;
    sitelinksMutationExecutor: SitelinksMutationExecutor;
    calloutsMutation: CalloutsMutation;
    phraseQueryProvider: PhrasesWizardDataProvider;
    incognitoDataProvider: IncognitoDataProvider;
    reportsDomainDataProvider: ReportsDomainDataProvider;
    retargetingDataProvider: RetargetingDataProvider;
    retargetingMutationExecutor: RetargetingMutationExecutor;
    minusPhraseLibraryDataProvider: MinusPhraseLibraryProvider;
    minusPhraseLibraryMutationExecutor: MinusPhraseMutationExecutor;
    dialogsDataProvider: DialogsDataProvider;
    dialogsMutationExecutor: DialogsMutationExecutor;
    goalsDataProvider: GoalsDataProvider;
    clientDevProvider: ClientDevProvider;
    organizationDataProvider: OrganizationDataProvider;
    organizationMutationExecutor: OrganizationMutationExecutor;
    productsDataProvider: ProductsDataProvider;
    marketersDataProvider: MarketersDataProvider;
    importProvider: ImportProvider;
    userSettingsDataProvider: UserSettingsDataProvider;
    predictorProvider: PredictorProvider;
    xlsDataProvider: XlsDataProvider;
    canvasApi: CanvasApi;
    regionsMutationExecutor: RegionsMutationExecutor;
    regionsDataProvider: RegionsDataProvider;
}

const createMiddleware = (
    apiManager: RestApiManager,
    history: History,
    epicMiddleware: EpicMiddleware<Action, Action, State, EpicDeps>
): StoreEnhancer => {
    let middlewares: Array<Middleware> = [
        createRavenMiddleware(Raven),
        middleware,
        epicMiddleware,
        metrikaMiddleware,
        rumMiddleWare,
        routesMiddleware
    ];

    if (window.Ya && Ya.Rum) {
        middlewares = [rumErrorCounterMiddleWare, ...middlewares];
    }

    return applyMiddleware(...middlewares);
};

export function getStoreForDevEnv(
    apiManager: RestApiManager,
    history: History,
    epicDeps: EpicDeps,
    initData: InitializationData
): Store<State> {
    const composeEnhancers = composeWithDevTools({
        serialize: true // иначе Map отобржаются как пустые объекты
    });

    const epicMiddleware = createEpicMiddleware<Action, Action, State, EpicDeps>({
        dependencies: epicDeps
    });

    const store = createStore<State, Action, {}, {}>(
        getRootReducer(initData),
        composeEnhancers(enhancer, createMiddleware(apiManager, history, epicMiddleware))
    );

    epicMiddleware.run(mainEpic);

    persistStore(store);

    return store;
}
